import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useEntity } from '../context/EntityContext';
import { apiPost } from '../api/client';

/* Emblema scout mundial oficial (WOSM): flor de lis, cuerda y dos estrellas */
const WOSMEmblem: React.FC<{ className?: string; size?: number }> = ({ className = '', size = 56 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 100 120"
    width={size}
    height={size * (120 / 100)}
    className={className}
    aria-hidden
  >
    {/* Cuerda circular con nudo al fondo */}
    <ellipse cx="50" cy="58" rx="42" ry="48" fill="none" stroke="currentColor" strokeWidth="3.5" />
    <path fill="currentColor" d="M38 98v-6c0-2 2-4 5-5l4-2 6 2 4 2c3 1 5 3 5 5v6h-4v-4c0-1-1-2-3-2l-4-1-5 1-4 1c-2 0-3 1-3 2v4z" />
    {/* Flor de lis central */}
    <path fill="currentColor" d="M50 18l8 28-8-4-8 4 8-28zm0 24v38l-14-28 14 7 14-7-14 28zm0-7l-18 7 11-14 7 4 7-4 11 14-18-7z" />
    {/* Dos estrellas de cinco puntas */}
    <path fill="currentColor" d="M22 52l2 6 6 1-4 5 1 6-5-3-5 3 1-6-4-5 6-1z" />
    <path fill="currentColor" d="M78 52l-2 6-6 1 4 5-1 6 5-3 5 3-1-6 4-5-6-1z" />
  </svg>
);

const BADEN_POWELL_QUOTE =
  'Intentad dejar este mundo un poco mejor de como os lo encontrasteis.';

const BADEN_POWELL_IMAGE_URL =
  'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Robert_Baden-Powell%2C_1st_Baron_Baden-Powell.jpg/200px-Robert_Baden-Powell%2C_1st_Baron_Baden-Powell.jpg';

const BadenPowellPhoto: React.FC = () => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="w-16 h-16 rounded-full bg-asde-purple/20 border-2 border-asde-purple/40 flex items-center justify-center shadow-md" aria-hidden>
        <span className="text-asde-purple font-bold text-lg">B-P</span>
      </div>
    );
  }
  return (
    <img
      src={BADEN_POWELL_IMAGE_URL}
      alt="Lord Robert Baden-Powell, fundador del escultismo"
      className="w-16 h-16 rounded-full object-cover border-2 border-asde-purple/30 shadow-md"
      onError={() => setFailed(true)}
    />
  );
};

const QuotePanel: React.FC = () => (
  <div className="scout-pattern min-h-[220px] md:min-h-full flex flex-col justify-center p-8 md:p-12 text-center md:text-left">
    <div className="flex justify-center md:justify-start mb-4">
      <WOSMEmblem className="text-asde-purple" size={56} />
    </div>
    <blockquote className="text-asde-purple-dark text-lg md:text-xl font-medium leading-relaxed max-w-md mx-auto md:mx-0">
      «{BADEN_POWELL_QUOTE}»
    </blockquote>
    <footer className="mt-4 flex flex-col md:flex-row items-center gap-3 justify-center md:justify-start">
      <BadenPowellPhoto />
      <div>
        <cite className="text-asde-purple not-italic font-semibold">
          Lord Robert Baden-Powell
        </cite>
        <p className="text-asde-purple-dark/80 text-sm">Fundador del escultismo</p>
      </div>
    </footer>
  </div>
);

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverSent, setRecoverSent] = useState(false);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const { login } = useAuth();
  const { setAllowedEntities } = useEntity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session') === 'expired';

  useEffect(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('tesoreria_token') : null;
    if (token) navigate('/', { replace: true });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const entitiesList = await login(username, password);
      setAllowedEntities(entitiesList);
      navigate('/welcome', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoverLoading(true);
    try {
      await apiPost<{ message: string }>('/api/v1/auth/forgot-password', { email: recoverEmail.trim() });
      setRecoverSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al enviar');
    } finally {
      setRecoverLoading(false);
    }
  };

  const   formCardClass =
    'w-full max-w-md rounded-2xl bg-asde-cream shadow-xl border border-asde-purple/20 overflow-hidden';

  const logoAsdecyl = `${process.env.PUBLIC_URL || ''}/logo-asdecyl.jpg`;

  if (showRecover) {
    return (
      <div className="min-h-screen scout-pattern flex flex-col md:flex-row">
        <div className="hidden md:block md:w-2/5 lg:w-2/5">
          <QuotePanel />
        </div>
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className={formCardClass}>
            <div className="bg-asde-purple px-6 py-4 flex items-center gap-3">
              <img src={logoAsdecyl} alt="ASDECYL" className="h-10 w-auto object-contain rounded" />
              <div>
                <h1 className="text-lg font-bold text-white">Tesorería Scouts</h1>
                <p className="text-asde-sand/90 text-sm">Recuperar contraseña</p>
              </div>
            </div>
            <div className="p-6">
              {recoverSent ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 bg-asde-sand/50 border border-asde-purple/20 rounded-xl px-4 py-3">
                    Si existe una cuenta con ese correo, recibirás un email con instrucciones para restablecer la contraseña.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowRecover(false); setRecoverSent(false); setRecoverEmail(''); setError(''); }}
                    className="w-full rounded-xl border-2 border-asde-purple/40 py-2.5 px-4 font-medium text-asde-purple-dark hover:bg-asde-purple/10 transition-colors"
                  >
                    Volver al inicio de sesión
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRecoverSubmit} className="space-y-4">
                  {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                      {error}
                    </div>
                  )}
                  <div>
                    <label htmlFor="recover-email" className="block text-sm font-medium text-asde-purple-dark">
                      Email de la cuenta
                    </label>
                    <input
                      id="recover-email"
                      type="email"
                      value={recoverEmail}
                      onChange={(e) => setRecoverEmail(e.target.value)}
                      required
                      className="mt-1 block w-full rounded-xl border-2 border-asde-purple/30 px-3 py-2.5 text-slate-800 focus:border-asde-purple focus:ring-2 focus:ring-asde-purple/30 transition-shadow"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={recoverLoading}
                    className="w-full rounded-xl bg-asde-purple py-2.5 px-4 font-semibold text-white hover:bg-asde-purple-light disabled:opacity-50 transition-colors shadow-md"
                  >
                    {recoverLoading ? 'Enviando...' : 'Enviar correo de recuperación'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowRecover(false); setError(''); }}
                    className="w-full rounded-xl border-2 border-asde-purple/40 py-2.5 px-4 font-medium text-asde-purple-dark hover:bg-asde-purple/10 transition-colors"
                  >
                    Volver al inicio de sesión
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
        <div className="md:hidden p-4 pt-2">
          <QuotePanel />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen scout-pattern flex flex-col md:flex-row">
      <div className="hidden md:block md:w-2/5 lg:w-2/5">
        <QuotePanel />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className={formCardClass}>
          <div className="bg-asde-purple px-6 py-5 flex items-center gap-3">
            <img src={logoAsdecyl} alt="ASDECYL" className="h-11 w-auto object-contain rounded" />
            <div>
              <h1 className="text-xl font-bold text-white">Tesorería Scouts</h1>
              <p className="text-asde-sand/90 text-sm">Iniciar sesión · ASDE Castilla y León</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {sessionExpired && !error && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                Tu sesión ha expirado. Vuelve a iniciar sesión.
              </div>
            )}
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-asde-purple-dark">
                Usuario
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="mt-1 block w-full rounded-xl border-2 border-asde-purple/30 px-3 py-2.5 text-slate-800 focus:border-asde-purple focus:ring-2 focus:ring-asde-purple/30 transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-asde-purple-dark">
                Contraseña
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="block w-full rounded-xl border-2 border-asde-purple/30 px-3 py-2.5 pr-11 text-slate-800 focus:border-asde-purple focus:ring-2 focus:ring-asde-purple/30 transition-shadow"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-asde-purple hover:text-asde-purple-dark rounded-lg hover:bg-asde-purple/10 transition-colors"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-asde-purple py-3 px-4 font-semibold text-white hover:bg-asde-purple-light disabled:opacity-50 transition-colors shadow-md"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowRecover(true)}
                className="text-sm text-asde-purple hover:text-asde-purple-dark font-medium hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="md:hidden p-4 pt-2">
        <QuotePanel />
      </div>
    </div>
  );
};

export default LoginPage;
